import { Checkbox } from "../beui/Checkbox";
import { useState } from "react";
import { api, ApiError, type ArrangementRequest, type AgreementDocument as Document, type PurchaseOfferReview } from "../../lib/api";
import { usePurchaseOfferRequirements } from "../../lib/usePurchaseOfferRequirements";
import { Modal } from "../beui/Modal";
import { AgreementDocument } from "../AgreementDocument";
import { PurchaseOfferFields } from "../PurchaseOfferFields";
import "../../pages/LegalHub.css";

export function SellerRequestAcceptance({ request, onClose, onCommit }: {
  request: ArrangementRequest; onClose: () => void; onCommit: (review?: PurchaseOfferReview) => Promise<void>;
}) {
  const loaded = usePurchaseOfferRequirements(true, request.itemId);
  const [review, setReview] = useState<PurchaseOfferReview>(() => ({ offerKey: crypto.randomUUID() }));
  const [document, setDocument] = useState<Document | null>(null), [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null), [uncertain, setUncertain] = useState(false);
  async function prepare() {
    setBusy(true); setError(null);
    try {
      const prepared = await api.previewPrivatePurchase(request.id, "accept", review);
      if (prepared.document) setDocument(prepared.document);
      else if (loaded.requirements?.active === false) { await onCommit(); onClose(); }
      else throw new Error("The current seller wording could not be loaded. Retry review.");
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Could not prepare the seller offer."); }
    finally { setBusy(false); }
  }
  async function commit() {
    if (!document || !reviewed || busy) return;
    setBusy(true); setError(null);
    try { await onCommit({ ...review, expectedDocumentId: document.id }); onClose(); }
    catch (failure) { setUncertain(!(failure instanceof ApiError) || failure.status >= 500); setError(failure instanceof Error ? failure.message : "The outcome is unknown. Retry this same acceptance."); }
    finally { setBusy(false); }
  }
  return <Modal className="message-form-editor" label="Review seller offer" onClose={() => { if (!busy && !uncertain) onClose(); }}>
    <header className="legal-reader-head"><h2 className="text-lg font-bold text-navy">Accept request · {request.itemTitle}</h2></header>
    <div className="legal-reader-body space-y-4">
      {document ? <><AgreementDocument document={document} /><Checkbox label="I offer these purchase terms" checked={reviewed} onCheckedChange={setReviewed} className="items-start" /><p className="text-xs text-mut">The buyer must review the purchase agreement at checkout. No payment or stock reservation yet.</p></>
        : loaded.requirements ? <PurchaseOfferFields value={review} onChange={setReview} inspection={loaded.requirements.fields.includes("inspection_schedule")} condition={loaded.requirements.missingFacts?.includes("item_condition")} /> : <p role="status">Loading offer requirements…</p>}
      {(error || loaded.error) && <p role="alert" className="text-sm text-brand-red">{error || loaded.error}</p>}
      {loaded.error && <button type="button" className="btn-outline" onClick={loaded.retry}>Retry loading</button>}
    </div>
    <footer className="legal-reader-foot flex flex-wrap justify-end gap-2">
      {document && !uncertain && <button type="button" disabled={busy} className="btn-outline" onClick={() => { setDocument(null); setReviewed(false); }}>Back to edit</button>}
      {(!document || uncertain) && <button type="button" disabled={busy || uncertain} className="btn-outline" onClick={onClose}>Cancel</button>}
      <button type="button" disabled={busy || !loaded.requirements || !!document && !reviewed} className="btn-red" onClick={() => void (document ? commit() : prepare())}>{busy ? "Saving…" : document ? "Accept request" : "Review offer"}</button>
    </footer>
  </Modal>;
}
